import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { HandHeart, HeartHandshake, Send, Copy, Check } from "lucide-react";

export default function Submission() {
  const { t, settings, lang } = useApp();
  const [tab, setTab] = useState("receiver");
  const [banks, setBanks] = useState([]);
  const [drops, setDrops] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const [donor, setDonor] = useState({
    name: "", contact: "", donation_type: "money",
    amount: "", items: "", drop_point_id: "", agreement: false,
  });
  const [receiver, setReceiver] = useState({
    kabupaten: "", kota: "", pic_name: "", pic_number: "", people_count: 0, needs: "", details: "",
  });

  useEffect(() => {
    api.get("/bank-accounts").then((r) => setBanks(r.data));
    api.get("/resources?category=drop_point").then((r) => setDrops(r.data));
  }, []);

  const copy = (text, idx) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  const submitDonor = async (e) => {
    e.preventDefault();
    if (!donor.agreement) { toast.error(t.submit.donor.agreement); return; }
    try {
      await api.post("/submissions/donor", donor);
      toast.success(t.submit.donor.success);
      setDonor({ name: "", contact: "", donation_type: "money", amount: "", items: "", drop_point_id: "", agreement: false });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const submitReceiver = async (e) => {
    e.preventDefault();
    try {
      await api.post("/submissions/receiver", { ...receiver, people_count: Number(receiver.people_count) || 0 });
      toast.success(t.submit.receiver.success);
      setReceiver({ kabupaten: "", kota: "", pic_name: "", pic_number: "", people_count: 0, needs: "", details: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <h1 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight">{settings?.[`submit_title_${lang}`] || t.submit.title}</h1>
      <p className="mt-2 text-[#141414]/70 max-w-xl text-sm">{settings?.[`submit_subtitle_${lang}`] || t.submit.subtitle}</p>

      <div className="grid grid-cols-2 gap-3 mt-6">
        <button
          onClick={() => setTab("receiver")}
          data-testid="tab-receiver"
          className={`flat-border rounded-2xl p-5 md:p-6 text-left transition-colors ${tab === "receiver" ? "bg-[#670003] text-[#fff0d2] border-[#670003]" : "bg-white"}`}
        >
          <HandHeart className="w-5 h-5 mb-2" strokeWidth={2} />
          <div className="font-display text-xl md:text-2xl">{t.submit.tabs.receiver}</div>
        </button>
        <button
          onClick={() => setTab("donor")}
          data-testid="tab-donor"
          className={`flat-border rounded-2xl p-5 md:p-6 text-left transition-colors ${tab === "donor" ? "bg-[#670003] text-[#fff0d2] border-[#670003]" : "bg-white"}`}
        >
          <HeartHandshake className="w-5 h-5 mb-2" strokeWidth={2} />
          <div className="font-display text-xl md:text-2xl">{t.submit.tabs.donor}</div>
        </button>
      </div>

      <div className="mt-6 flat-border rounded-2xl bg-white p-6 md:p-8">
        {tab === "receiver" ? (
          <form onSubmit={submitReceiver} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["kabupaten", t.beneficiaries.fields.kabupaten],
              ["kota", t.beneficiaries.fields.kota],
              ["pic_name", t.beneficiaries.fields.pic_name],
              ["pic_number", t.beneficiaries.fields.pic_number],
            ].map(([k, l]) => (
              <div key={k}>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{l}</label>
                <input required value={receiver[k]} onChange={(e) => setReceiver({ ...receiver, [k]: e.target.value })} data-testid={`rec-${k}`} className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
              </div>
            ))}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.beneficiaries.fields.people_count}</label>
              <input type="number" min="0" value={receiver.people_count} onChange={(e) => setReceiver({ ...receiver, people_count: e.target.value })} data-testid="rec-people" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.submit.receiver.needs}</label>
              <input required value={receiver.needs} onChange={(e) => setReceiver({ ...receiver, needs: e.target.value })} data-testid="rec-needs" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.beneficiaries.fields.details}</label>
              <textarea rows={3} value={receiver.details} onChange={(e) => setReceiver({ ...receiver, details: e.target.value })} data-testid="rec-details" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
            </div>
            <div className="md:col-span-2">
              <button type="submit" data-testid="submit-receiver" className="flat-btn-primary rounded-full px-6 py-2.5 font-semibold text-sm flex items-center gap-2">
                <Send className="w-4 h-4" strokeWidth={2} /> {t.submit.receiver.submit}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitDonor} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.submit.donor.name}</label>
                <input required value={donor.name} onChange={(e) => setDonor({ ...donor, name: e.target.value })} data-testid="don-name" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.submit.donor.contact}</label>
                <input required value={donor.contact} onChange={(e) => setDonor({ ...donor, contact: e.target.value })} data-testid="don-contact" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.submit.donor.type}</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {["money", "goods"].map((k) => (
                  <button key={k} type="button" data-testid={`don-type-${k}`} onClick={() => setDonor({ ...donor, donation_type: k })}
                    className={`flat-border rounded-lg px-4 py-2.5 font-semibold text-sm transition-colors ${donor.donation_type === k ? "bg-[#670003] text-[#fff0d2] border-[#670003]" : "bg-white"}`}>
                    {t.submit.donor[k]}
                  </button>
                ))}
              </div>
            </div>

            {donor.donation_type === "money" ? (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.submit.donor.amount}</label>
                  <input required value={donor.amount} onChange={(e) => setDonor({ ...donor, amount: e.target.value })} data-testid="don-amount" placeholder="Rp 500.000" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
                </div>
                <div className="flat-border rounded-lg p-4 bg-[#fff0d2]">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">{t.submit.donor.bank}</div>
                  <div className="space-y-2">
                    {banks.map((b, i) => (
                      <div key={b.id} data-testid={`bank-${b.id}`} className="bg-white flat-border rounded-lg px-3 py-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {b.logo_url ? (
                            <img src={b.logo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${b.logo_url}` : b.logo_url} alt={b.bank_name} className="w-10 h-10 object-contain flat-border rounded-md bg-[#fff0d2] p-1 shrink-0" onError={(e)=>{e.target.style.display='none';}} />
                          ) : (
                            <div className="w-10 h-10 flat-border rounded-md bg-[#fff0d2] flex items-center justify-center shrink-0 text-[10px] font-bold">{b.bank_name.slice(0,3).toUpperCase()}</div>
                          )}
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{b.bank_name}</div>
                            <div className="text-sm truncate">{b.account_number} · {b.account_holder}</div>
                          </div>
                        </div>
                        <button type="button" data-testid={`bank-copy-${b.id}`} onClick={() => copy(b.account_number, i)} className="flat-btn bg-transparent rounded-full p-1.5 shrink-0">
                          {copiedIdx === i ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.submit.donor.items}</label>
                  <textarea required rows={2} value={donor.items} onChange={(e) => setDonor({ ...donor, items: e.target.value })} data-testid="don-items" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.submit.donor.drop}</label>
                  <select required value={donor.drop_point_id} onChange={(e) => setDonor({ ...donor, drop_point_id: e.target.value })} data-testid="don-drop" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none bg-white">
                    <option value="">— Pilih / Choose —</option>
                    {drops.map((d) => <option key={d.id} value={d.id}>{d.name} — {d.location}</option>)}
                  </select>
                </div>
              </div>
            )}

            <label className="flex items-start gap-3 flat-border rounded-lg p-3 cursor-pointer bg-[#fff0d2]">
              <input type="checkbox" checked={donor.agreement} onChange={(e) => setDonor({ ...donor, agreement: e.target.checked })} data-testid="don-agreement" className="mt-1 w-4 h-4 accent-[#670003]" />
              <span className="text-sm">{t.submit.donor.agreement}</span>
            </label>

            <button type="submit" data-testid="submit-donor" className="flat-btn-primary rounded-full px-6 py-2.5 font-semibold text-sm flex items-center gap-2">
              <Send className="w-4 h-4" strokeWidth={2} /> {t.submit.donor.submit}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
